import React, { useContext, useEffect, useState } from "react";
import uuid from "react-uuid";
import FlowContext from "../../../../contexts/flowContext";
import SimulationContext from "../../../../contexts/simulation";
import NotificationContext from "../../../../contexts/notification";
import UserContext from "../../../../contexts/user";
import FetchContext from "../../../../contexts/fetch";
import UseConfirmation from "../../../../hooks/useConfirmation";
import UseOnSaveFlow from "../UseOnSaveFlow";

const CopyPasteNodes = ({ rfInstance }) => {
  const [, notificationDispatcher] = useContext(NotificationContext);
  const [flowState, flowDispatcher] = useContext(FlowContext);
  const [simulationState] = useContext(SimulationContext);
  const [fetchState] = useContext(FetchContext);
  const [userState] = useContext(UserContext);

  const [nodesFromClipboard, setNodesFromClipboard] = useState([]);
  const [edgesFromClipboard, setEdgesFromClipboard] = useState([]);

  const { onSave } = UseOnSaveFlow();

  useEffect(() => {
    if (rfInstance) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    rfInstance,
    flowState.editionInProgress,
    simulationState.active,
    flowState,
    userState,
  ]);

  useEffect(() => {
    if (nodesFromClipboard.length) {
      flowDispatcher({
        type: "update-node-list",
        value: [...flowState.nodes, ...nodesFromClipboard],
      });
    }
  }, [nodesFromClipboard]);

  useEffect(() => {
    if (edgesFromClipboard.length) {
      flowDispatcher({
        type: "update-edge-list",
        value: [...flowState.edges, ...edgesFromClipboard],
      });
    }
  }, [edgesFromClipboard]);

  const saveWithConfirmation = UseConfirmation(
    {
      title: "Confirmer la sauvegarde",
      message: "La sauvegarde écrasera l'ancienne version. Continuer ?",
      onOk: onSave,
    },
    fetchState.loadingSaveApi
  );

  const handleKeyDown = (event) => {
    if (flowState.editionInProgress || simulationState.active) {
      return;
    }

    let charCode = String.fromCharCode(event.which).toLowerCase();

    if ((event.ctrlKey || event.metaKey) && charCode === "s") {
      event.preventDefault();
      saveWithConfirmation();
    }

    //copy to clipboard
    else if ((event.ctrlKey || event.metaKey) && charCode === "c") {
      const flow = rfInstance.toObject();

      let edges = [];
      const nodes = flow.nodes.filter((node) => node.selected);

      // get all edges connected to nodes
      nodes.forEach((node) => {
        edges = edges.concat(
          flow.edges.filter(
            (edge) =>
              (edge.source === node.id || edge.target === node.id) &&
              !edges.includes(edge)
          )
        );
      });

      // get only edges with connected source and target
      edges = edges.filter((edge) => {
        return (
          nodes.find((node) => node.id === edge.source) &&
          nodes.find((node) => node.id === edge.target)
        );
      });

      if (nodes.length) {
        navigator.clipboard
          .writeText(JSON.stringify({ nodes, edges }))
          .then((r) => {
            notificationDispatcher({
              type: "notify",
              notification: {
                id: uuid(),
                type: "success",
                message: `${nodes.length} node${
                  nodes.length > 1 ? "s" : ""
                } copied to clipboard`,
                duration: 3000,
              },
            });
          })
          .catch((err) => {
            notificationDispatcher({
              type: "notify",
              notification: {
                id: uuid(),
                type: "failure",
                message: "Copy feature not available",
                duration: 3000,
              },
            });
          });
        event.preventDefault();
      }
    }
    //paste from clipboard
    else if ((event.ctrlKey || event.metaKey) && charCode === "v") {
      try {
        navigator.clipboard
          .readText()
          .then((text) => {
            try {
              const clipboardContent = JSON.parse(text);
              let edges = clipboardContent.edges;
              let nodes = clipboardContent.nodes.map((node) => {
                const { selected, ...rest } = node;
                const newNodeId = uuid();
                edges = edges.map((edge) => {
                  return {
                    ...edge,
                    source: edge.source === node.id ? newNodeId : edge.source,
                    target: edge.target === node.id ? newNodeId : edge.target,
                  };
                });

                return {
                  ...rest,
                  id: newNodeId,
                  data: {
                    ...node.data,
                    id: newNodeId,
                    starter: false,
                  },
                  position: {
                    x: node.position.x + 100,
                    y: node.position.y + 100,
                  },
                };
              });
              edges = edges.map((edge) => {
                const { selected, ...rest } = edge;
                return {
                  ...rest,
                  id: `reactflow__edge-${edge.source}-${edge.target}`,
                  animated: false,
                };
              });
              nodes = nodes.map((node) => {
                return {
                  ...node,
                  nextNodeListConditions: edges.reduce((pv, cv) => {
                    if (cv.source === node.id) {
                      pv.push(cv.id);
                      return pv;
                    }
                    return pv;
                  }, []),
                };
              });
              setEdgesFromClipboard(edges);
              setNodesFromClipboard(nodes);
            } catch (e) {
              notificationDispatcher({
                type: "notify",
                notification: {
                  id: uuid(),
                  type: "failure",
                  message: "Invalid clipboard content",
                  duration: 3000,
                },
              });
            }
          })
          .catch((err) => {
            // Navigator not supporting clipboard API
            notificationDispatcher({
              type: "notify",
              notification: {
                id: uuid(),
                type: "failure",
                message: "An error occurred while reading clipboard",
                duration: 3000,
              },
            });
          });
      } catch (e) {
        notificationDispatcher({
          type: "notify",
          notification: {
            id: uuid(),
            type: "failure",
            message: "Paste feature not available on this browser",
            duration: 3000,
          },
        });
      }
      event.preventDefault();
    }
    //Select all nodes and edges
    else if ((event.ctrlKey || event.metaKey) && charCode === "a") {
      flowDispatcher({
        type: "select-all-nodes",
      });
      flowDispatcher({
        type: "select-all-edges",
      });
      event.preventDefault();
    }
    //unselect all nodes and edges
    else if (event.key === "Escape") {
      flowDispatcher({
        type: "unselect-all-nodes",
      });
      flowDispatcher({
        type: "unselect-all-edges",
      });
      event.preventDefault();
    }
  };

  return <></>;
};

export default CopyPasteNodes;
